import type { CardSummary as Card, CardInfo, Scan } from "@hobbyshopify/backend-client";

/**
 * How a card reads in a list.
 *
 * A `Card` and a `CardInfo` describe the same thing in two vocabularies — the
 * confirmed row in camelCase, the model's reading in snake_case — and a scan
 * carries one, the other, or both. Every screen needs the same sentence out of
 * whichever it has, so the assembly lives here rather than in each row.
 */

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

/** "PSA 10", or "Raw" for an ungraded copy — the distinction is now identity. */
export function gradeLabel(card: Pick<Card, "gradingCompany" | "grade" | "slabbed">): string {
  if (!card.gradingCompany && !card.grade) return "Raw";
  const grade = card.grade % 1 === 0 ? String(card.grade) : card.grade.toFixed(1);
  return [card.gradingCompany, grade].filter(Boolean).join(" ") || "Raw";
}

function assemble(parts: (string | number | null | undefined)[], cardNumber?: string | null): string {
  const line = parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(" ");
  const number = cardNumber?.trim();
  return [line, number ? `#${number.replace(/^#/, "")}` : ""].filter(Boolean).join(" ");
}

export function cardInfoTitle(info: CardInfo | null | undefined): string {
  if (!info) return "Unidentified card";
  // card_full_name is the model's own one-liner and reads better than anything
  // reassembled from the parts, so it wins whenever it is present.
  if (info.card_full_name?.trim()) return info.card_full_name.trim();
  if (info.title?.trim()) return info.title.trim();
  const title = assemble([info.year, info.brand, info.player || info.team], info.card_number);
  return title || "Unidentified card";
}

export function cardTitle(card: Card): string {
  if (card.cardFullName?.trim()) return card.cardFullName.trim();
  const title = assemble([card.year || null, card.brand, card.player || card.team], card.cardNumber);
  return title || "Untitled card";
}

/**
 * The confirmed card's title when there is one, else the reading's.
 *
 * A confirmed scan is shown as the card it became rather than as what the model
 * guessed, so a corrected reading stops showing the wrong name the moment it is
 * corrected.
 */
export function scanTitle(scan: Scan): string {
  return scan.card ? cardTitle(scan.card) : cardInfoTitle(scan.cardInfo);
}

/** The second line: set, parallel and grade, whichever of them exist. */
export function cardSubtitle(scan: Scan): string {
  const info = scan.cardInfo;
  const parts = scan.card
    ? [scan.card.setName, scan.card.parallelType, gradeLabel(scan.card)]
    : [info?.set_name, info?.parallel_type, info?.grade];
  return parts
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(" · ");
}

/** The image to show for a scan: its own photograph, else the card's. */
export function scanImage(scan: Scan): string | null {
  return scan.imageUrl || scan.card?.imageUrl || null;
}

/**
 * A card field's name, for the review queue's difference list.
 *
 * Keyed on the `CardSummary` field names the API reports differences under, so
 * adding a field to the comparison shows up here as a missing label rather than
 * as a wrong one.
 */
const FIELD_LABELS: Record<string, string> = {
  player: "Player",
  team: "Team",
  brand: "Brand",
  year: "Year",
  cardNumber: "Number",
  parallelType: "Parallel",
  insertName: "Insert",
  cardType: "Type",
  gradingCompany: "Grader",
  grade: "Grade",
  slabbed: "Slabbed",
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}
